(function () {
    'use strict';

    angular
        .module('app')
        .factory('EmailService', EmailService);

    EmailService.$inject = [
        '$rootScope',
        '$http',
        '$q',
        'UrlService',
        'Upload',
        'DataService',
        'EmailAddressService',
        'uuid'];

    function EmailService(
        $rootScope,
        $http,
        $q,
        UrlService,
        Upload,
        DataService,
        EmailAddressService,
        uuid) {
        var emailDraftDelay = 30000;
        var resourceUrl = 'email';
        var service = {};

        // When the location is changed, the blocked email address cache should be cleared, since this is a per view cache and not per session.
        $rootScope.$on('$locationChangeSuccess', function () {
            if (!_.isNil(validateEmailAddress.addressCache)) {
                delete validateEmailAddress.addressCache;
            }
        });

        angular.extend(service, DataService(resourceUrl));

        angular.extend(service, {
            getEmailDraftDelay: getEmailDraftDelay,
            getEmail: getEmail,
            sendEmail: sendEmail,
            save: save,
            getEmailAttachment: getEmailAttachment,
            saveEmailAttachment: saveEmailAttachment,
            cancelSaveEmailAttachment: cancelSaveEmailAttachment,
            copyChilliDocsFileToEmail: copyChilliDocsFileToEmail,
            deleteEmailAttachment: deleteEmailAttachment,
            validateEmailAddress: validateEmailAddress,
            createResponse: createResponse,
            getEmailRelatedEntities: getEmailRelatedEntities,
            getEmailContacts: getEmailContacts,
            getConversation: getConversation,
            getPreview: getPreview,
            getValidationReport: getValidationReport
        });

        return service;

        /**
         * Returns the delay for saving of email drafts.
         *
         * @return {promise} Promise resolves returning integer for the delay period.
         */
        function getEmailDraftDelay() {
            return $q.when(emailDraftDelay);
        }

        function getEmail(emailId, operation) {
            var requestUrl = UrlService.getWebAPIUrl() + '/email/' + emailId;

            if (angular.isDefined(operation) && operation !== null && operation !== '') {
                requestUrl += '?operation=' + operation;
            }

            return $http.get(requestUrl).then(
                function ok(response) { return response.data; },
                function error(response) { return $q.reject(response); });
        }

        /**
         * Returns a list of entity ids which are linked to the email.
         *
         * @return {promise} Promise resolves returning a list of integers upon success.
         */
        function getEmailRelatedEntities(emailId) {
            var requestUrl = UrlService.getWebAPIUrl() + '/email/' + emailId + '/LinkedEntities';
            return $http.get(requestUrl).then(
                function ok(response) { return response.data; }, null);
        }

        /**
         * Returns a list of email contacts for the specified email.
         *
         * @param {int} emailId - Id of email for which the email contacts will be retrieved.
         * @param {bool} includeReferenceContacts - Should the reference contacts also be retrieved, these are used for the To addresses when in 'multiple from address' mode.
         * @return {promise} Promise resolves returning a list of email contacts upon success.
         */
        function getEmailContacts(emailId, includeReferenceContacts) {
            var requestUrl = UrlService.getWebAPIUrl() + '/email/' + emailId + '/Contacts';

            if (!_.isNil(includeReferenceContacts)) {
                requestUrl += '?includereferencecontacts=' + includeReferenceContacts;
            }

            return $http.get(requestUrl).then(
                function ok(response) { return response.data; }, null);
        }

        /**
         * Save an existing email, or create a new one.
         * Does not save attachments.
         *
         * @param {object} data - The email data to save.
         *
         * @returns {promise} If rejected, returns HTTP response, if resolved, returns an object with the following properties:
         *   {number} Id
         *   {number} UpdatedBy - User id of the user that saved the email.
         *   {date} UpdatedDate - DateTime the email was saved.
         */
        function save(data) {
            var requestUrl = UrlService.getWebAPIUrl() + '/' + resourceUrl + '/';

            if (data.Id || data.id) {
                var id = data.Id ? data.Id : data.id;
                return $http.put(requestUrl + id, data)
                    .then(function (response) {
                        return response.data;
                    });
            } else {
                return $http.post(requestUrl, data)
                    .then(function (response) {
                        return response.data;
                    });
            }
        }

        /**
         * POST to create an email response
         *
         * Creates a new email as a response to an existing email.
         *
         * @param {number} emailId - Email which is being responded
         * @param {number} fromEntityId - Entity which the response email will be from
         * @param {string} responseType - The type of response. Reply, ReplyAll or Forward.
         *
         * @return {Object} An object describing the email. Currently this is the 'Id' property.
         */
        function createResponse(emailId, fromEntityId, responseType) {
            var requestUrl = UrlService.getWebAPIUrl() + '/' + resourceUrl + '/' + emailId + '/createresponse';
            var data = { Type: responseType, FromEntityId: fromEntityId };
            return $http.post(requestUrl, data).then(
                function ok(response) { return response.data; }, null);
        }

        /**
         * POST to a new 'send' for an existing email
         *
         * If the email for the specified id does not exist an error will be expected.
         *
         * @param {number} emailId - Id of email which is to be sent
         * @param {array} sendEmails - The emails to be sent from the draft and their recipients. Optional. If not supplied all possible emails will be sent from the draft.
         *
         * @return returns the response data
         */
        function sendEmail(emailId, sendEmails) {
            return $http.post(UrlService.getWebAPIUrl() + '/email/' + emailId + '/send/', sendEmails)
                .then(function (response) {
                    // OK
                    return response.data;
                }, function (response) {
                    return $q.reject(response);
                });
        }

        /**
         * Returns a list of emails related to the conversation of an email.
         *
         * @param {number} emailId - Id of the email to which the conversation is linked
         *
         * @return {promise} Promise resolves returning a list of 'email data' for the conversation.
         */
        function getConversation(emailId) {
            var requestUrl = UrlService.getWebAPIUrl() + '/email/' + emailId + '/Conversation';
            return $http.get(requestUrl).then(
                function ok(response) { return response.data; }, null);
        }

        /**
         * Returns the specified email attachment if it exists, otherwise an error will be expected
         *
         * @param {number} emailId - Id of the email to which the attachment belongs
         * @param {number} documentId - Id of the document which is associated with the required attachment
         *
         * @return {promise} Promise resolves returning an blob containing the requested file.
         */
        function getEmailAttachment(emailId, documentId) {
            var req = {
                method: 'GET',
                url: UrlService.getWebAPIUrl() + '/email/' + emailId + '/attachment/' + documentId,
                responseType: 'arraybuffer'
            };

            return $http(req).then(getEmailAttachmentOk);

            function getEmailAttachmentOk(response) {
                return new Blob([response.data], { type: response.headers('Content-Type') });
            }
        }

        /**
         * Returns a 'preview' for the email, which is email data with all of the send pre-processing conducted on the email.
         *
         * @param {number} emailId - Id of the email for which the preview data is to be retrieved
         * @param {number} entityId - Id of the entity to be used for the merge field data. Must be an entity contained within the From addresses.
         *
         * @return {promise} Promise resolves returning email data.
         */
        function getPreview(emailId, entityId) {
            var requestUrl = UrlService.getWebAPIUrl() + '/email/' + emailId + '/Preview';

            if (!_.isNil(entityId) && entityId !== '') {
                requestUrl += '?entityId=' + entityId;
            }

            return $http.get(requestUrl).then(
                function ok(response) { return response.data; });
        }

        /**
         * Returns an email validation report for the supplied email id.
         *
         * @param {number} emailId - Id of the email for which the validation report is to be returned.
         *
         * @return {promise} Promise resolves returning an email validation report as a list of objects in the structure found at ChilliDocs.Models.Email.EmailSendReport.cs.
         */
        function getValidationReport(emailId) {
            var requestUrl = UrlService.getWebAPIUrl() + '/email/' + emailId + '/Validate';

            return $http.get(requestUrl).then(
                function ok(response) { return response.data; });
        }

        /**
         * Saves the supplied file data as an attachment for the specified email
         *
         * @param {number} emailId - Id of the email to which the file data will be saved
         * @param {object} file - An object which contains file meta information and has a property of File which is either a File blob.
         *
         * @return {promise} Promise resolves returning the 'File Details' for the newly saved attachment.
         */
        function saveEmailAttachment(emailId, file) {
            file.SaveRequestGuid = uuid.v4();
            var progress = 0;
            var promise = null;
            var requestUrl = UrlService.getWebAPIUrl() + '/email/' + emailId + '/attachment/' + file.SaveRequestGuid;

            if (file.File instanceof File) {
                var uploadPromise = Upload.http({
                    headers: {
                        'Content-Type': 'application/octet-stream',
                        'Content-Disposition': 'form-data; name="file"; filename="' + file.FileName +'"'
                    },
                    url: requestUrl,
                    data: file.File
                });
                
                uploadPromise.progress(function (evt) {
                    file.UploadProgress = parseInt(100.0 * evt.loaded / evt.total);
                });

                file.uploadPromise = uploadPromise;

                promise = uploadPromise.then(function (response) {
                    if (response.status >= 200 && response.status <= 299) {
                        return {
                            DocumentInformationId: response.data,
                            EmailId: emailId,
                            FileName: file.File.name,
                            FileSize: file.File.size
                        };
                    } else {
                        $q.reject($http(response));
                    }
                }, null);
            }

            return promise;
        }

        /**
         * Cancels the save attachment upload request for the specified email id and file
         *
         * @param {number} emailId - Id of the email to which the file data was being saved
         * @param {object} file - An object which contains file meta information and has a property of File which is either a File blob.
         *
         * @return {promise} Promise that will be resolved when the cancellation request succeeds or fails.
         */
        function cancelSaveEmailAttachment(emailId, file) {
            var requestUrl = UrlService.getWebAPIUrl() + '/email/' + emailId + '/uploads/' + file.SaveRequestGuid + '/cancel/';

            return $http.post(requestUrl);
        }

        /**
         * Copies the specified ChilliDocs files to the specified email as an attachment
         *
         * @param {number} emailId - Id of the email to which the copied file is to be saved
         * @param {object} file - 'File Details' of the ChilliDocs File which is to be copied
         *
         * @return {promise} Promise resolves returning the 'File Details' for the newly saved attachment.
         */
        function copyChilliDocsFileToEmail(emailId, file) {
            return $http.post(UrlService.getWebAPIUrl() + '/email/' + emailId + '/copychillidocsfile/' + file.FileId)
                .then(function (response) {
                    return {
                        DocumentInformationId: response.data,
                        EmailId: emailId,
                        FileName: file.name,
                        FileSize: file.size
                    };
                }, null);
        }

        /**
         * Deletes the specified email attachment
         *
         * @param {number} emailId - Id of the email to which the file data will be saved
         * @param {number} documentId - Document Id of the attachment which is to be deleted
         *
         * @return {promise} Promise resolves deleting the specified email attachment data.
         */
        function deleteEmailAttachment(emailId, documentId) {
            var req = {
                method: 'DELETE',
                url: UrlService.getWebAPIUrl() + '/email/' + emailId + '/attachment/' + documentId
            };

            return $http(req).then();
        }

        /**
         * Validates the supplied email address.
         * This will check the email format as well checking if it has been blocked.
         * The email address details for the tenancy will be lazy loaded into a cache.
         *
         * @param {text} emailAddress - Email address to validate
         *
         * @return {object} Returns an object with the following properties:
         *              {bool} Valid - True if the email address is valid
         *              {string} Message - Messsage describing a validation failure, e.g. 'Email Address is blocked.'
         */
        function validateEmailAddress(emailAddress) {
            if (_.isNil(validateEmailAddress.addressCache) && _.isNil(validateEmailAddress.getAddressesPromise)) {
                validateEmailAddress.getAddressesPromise = EmailAddressService.query({ Blocked: true })
                    .then(function (data) {
                        validateEmailAddress.addressCache = data.EmailAddresses;
                        delete validateEmailAddress.getAddressesPromise;
                    });
            }

            var result = { Valid: true };

            var emailRegEx = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
            if (!emailRegEx.test(emailAddress)) {
                result.Valid = false;
                result.Message = 'Email Address format is invalid.';
            } else if (_.some(validateEmailAddress.addressCache, function (emailAddressCache) { return emailAddress === emailAddressCache.Address && emailAddressCache.Blocked; })) {
                result.Valid = false;
                result.Message = 'Email Address is blocked.';
            }

            return result;
        }
    }
})();